"use client"

import { useState } from "react"
import { AuthGuard } from "@/components/auth-guard"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/toast"
import { callApi } from "@/lib/api-client"

const CATEGORY_OPTIONS = [
  "Bug",
  "Feature request",
  "UX / UI",
  "Billing",
  "Other",
] as const

export default function FeedbackPage() {
  const { addToast } = useToast()
  const [category, setCategory] = useState<(typeof CATEGORY_OPTIONS)[number]>("Other")
  const [subject, setSubject] = useState("")
  const [message, setMessage] = useState("")
  const [isSending, setIsSending] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!subject.trim() || !message.trim()) {
      addToast({
        title: "Missing info",
        message: "Please add a subject and message.",
        variant: "error",
      })
      return
    }

    try {
      setIsSending(true)
      await callApi("feedback", "submit", {
        category,
        subject: subject.trim(),
        message: message.trim(),
        page_url: typeof window !== "undefined" ? window.location.href : "",
      })

      addToast({
        title: "Feedback sent",
        message: "Thanks — we received your feedback.",
        variant: "success",
      })

      setSubject("")
      setMessage("")
      setCategory("Other")
    } catch (err: any) {
      addToast({
        title: "Failed to send",
        message: err?.message || "Please try again in a moment.",
        variant: "error",
      })
    } finally {
      setIsSending(false)
    }
  }

  return (
    <AuthGuard redirectTo="/login" requireAuth>
      <div className="p-6 md:p-10">
        <div className="max-w-2xl">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Submit feedback</h1>
          <p className="mt-2 text-muted-foreground">
            Tell us what’s working, what’s missing, or what feels off. Your message will be emailed to{" "}
            <span className="font-medium text-foreground">no-reply@gameit.lol</span>.
          </p>
        </div>

        <Card className="mt-6 max-w-2xl">
          <CardHeader>
            <CardTitle>Your message</CardTitle>
            <CardDescription>
              Include as much detail as you can (what you expected vs what happened).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <select
                  id="category"
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as any)}
                  disabled={isSending}
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g. Billing page: invoice download missing"
                  disabled={isSending}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="What happened? What did you expect to happen? Any steps to reproduce?"
                  rows={7}
                  disabled={isSending}
                />
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button type="submit" disabled={isSending}>
                  {isSending ? "Sending..." : "Send feedback"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AuthGuard>
  )
}

